import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "../../lib/utils";

export interface DisplayWorkspaceProps {
  /** Visibility & State */
  isOpen: boolean;

  /** Header Identity */
  title: string;
  subtitle?: string | null;

  /** Actions */
  onClose: () => void;
  headerActions?: ReactNode;

  /** Content */
  children: ReactNode;

  /** Sidebar (Optional) */
  sidebar?: ReactNode;

  /**
   * Optional full-width band rendered between the header and the scrollable content area.
   * Renders outside the max-width container and the scroll padding — ideal for mobile-only
   * summary strips (e.g. `lg:hidden`) that need to span edge-to-edge.
   */
  topBand?: ReactNode;

  /** Styling */
  className?: string;
  maxContentWidth?: string;

  /** When true the content area has no padding and does not scroll — the children own the layout */
  fillContent?: boolean;
}

/**
 * DisplayWorkspace Blueprint
 * 
 * CODIFIED BLUEPRINT for immersive read-only or informational worksurfaces.
 * Enforces the "Immersive Designer" aesthetic without the editing/wizard overhead:
 * 1. Workspace-filling layout.
 * 2. Immersive header with prominent Back action.
 * 3. Optional sidebar for metadata or high-level status.
 */
export function DisplayWorkspace({
  isOpen,
  title,
  subtitle,
  onClose,
  headerActions,
  children,
  sidebar,
  topBand,
  className,
  maxContentWidth = "max-w-[1400px]",
  fillContent = false,
}: DisplayWorkspaceProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative z-50 flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background animate-in fade-in duration-300",
        className
      )}
    >
      {/* Immersive Header */}
      <header className="shrink-0 border-b border-border/60 bg-muted/5 px-4 py-2 sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          {/* Left: close + identity */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
            <button
              onClick={onClose}
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 transition-all hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-black tracking-tight sm:text-xl">{title}</h1>
              {subtitle && (
                <p className="hidden truncate text-[10px] font-bold text-muted-foreground/80 sm:block">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right: actions */}
          {headerActions && (
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
              {headerActions}
            </div>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {sidebar && (
          <aside className="hidden lg:flex min-h-0 w-80 shrink-0 flex-col gap-8 overflow-y-auto border-r border-border/40 bg-muted/5 p-6">
            {sidebar}
          </aside>
        )}

        {/* Main Content Area */}
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
          {/* Full-width top band — sits outside the scroll container and max-width cap.
              Ideal for mobile-only summary strips (lg:hidden) that must span edge-to-edge. */}
          {topBand}
          {fillContent ? (
            <div className="min-h-0 flex-1 overflow-hidden">
              {children}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
              <div className={cn("mx-auto w-full", maxContentWidth)}>
                {children}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
